import React, { useEffect, useMemo, useState } from 'react';
import { Autocomplete, Box, TextField, Typography } from '@mui/material';
import type { StrapiCustomerRow } from '../api/strapiCrm';
import { useDebouncedValue } from '../hooks/useDebouncedValue';
import { customersSearchMinLength, useCustomersSearchQuery } from '../query/hooks';

const SEARCH_DEBOUNCE_MS = 320;

function dedupeCustomers(rows: StrapiCustomerRow[]): StrapiCustomerRow[] {
  const seen = new Set<string>();
  const out: StrapiCustomerRow[] = [];
  for (const r of rows) {
    if (!r.documentId || seen.has(r.documentId)) continue;
    seen.add(r.documentId);
    out.push(r);
  }
  return out;
}

/** Label ringkas untuk input & getOptionLabel. */
export function customerSearchOptionLabel(o: StrapiCustomerRow | null | undefined): string {
  if (!o?.documentId) return '';
  const num = o.customer_number?.trim();
  const name = o.name?.trim();
  if (num && name) return `${num} — ${name}`;
  return num || name || o.documentId;
}

function customerLocationLine(o: StrapiCustomerRow): string | null {
  const parts = [o.address?.trim(), o.city?.trim(), o.province?.trim(), o.postal_code?.trim()].filter(Boolean);
  return parts.length > 0 ? parts.join(', ') : null;
}

function customerPhoneLine(o: StrapiCustomerRow): string | null {
  const phone = o.mobile_wa?.trim() || o.phone?.trim() || o.office_phone?.trim();
  return phone ? phone : null;
}

type Props = {
  jwt: string | null;
  value: StrapiCustomerRow | null;
  onChange: (customer: StrapiCustomerRow | null) => void;
  disabled?: boolean;
  error?: string;
  inputTestId?: string;
  label?: string;
  required?: boolean;
};

/** Autocomplete pelanggan: pencarian server setelah 1 karakter, opsi multi-baris (alamat, kontak, dll.). */
export function CustomerSearchAutocomplete({
  jwt,
  value,
  onChange,
  disabled = false,
  error,
  inputTestId,
  label = 'Pelanggan',
  required = false,
}: Props) {
  const [inputValue, setInputValue] = useState('');
  const debouncedSearch = useDebouncedValue(inputValue, SEARCH_DEBOUNCE_MS);
  const qTrim = debouncedSearch.trim();
  const minLen = customersSearchMinLength();
  const searchQ = useCustomersSearchQuery(jwt, debouncedSearch);

  useEffect(() => {
    if (!value?.documentId) {
      setInputValue('');
      return;
    }
    setInputValue(customerSearchOptionLabel(value));
  }, [value?.documentId, value]);

  const options = useMemo(() => {
    const fromApi = qTrim.length >= minLen ? (searchQ.data?.rows ?? []) : [];
    const merged = [...(value?.documentId ? [value] : []), ...fromApi];
    return dedupeCustomers(merged);
  }, [value, qTrim.length, minLen, searchQ.data?.rows]);

  const searchLoading = qTrim.length >= minLen && searchQ.isFetching;
  const searchError =
    qTrim.length >= minLen && searchQ.isError
      ? searchQ.error instanceof Error
        ? searchQ.error.message
        : 'Gagal mencari pelanggan.'
      : undefined;

  const hintParts: string[] = [];
  if (qTrim.length > 0 && qTrim.length < minLen) {
    hintParts.push(`Ketik setidaknya ${minLen} karakter untuk mencari.`);
  } else if (qTrim.length >= minLen && searchLoading) {
    hintParts.push('Mencari…');
  }
  if (searchError) hintParts.push(searchError);
  const searchHint = hintParts.length > 0 ? hintParts.join(' ') : undefined;
  const mergedHelper = error ?? searchHint;

  return (
    <Autocomplete<StrapiCustomerRow, false, false, false>
      options={options}
      loading={searchLoading}
      loadingText="Memuat…"
      filterOptions={(x) => x}
      disabled={disabled || !jwt}
      value={value}
      onChange={(_, v) => onChange(v)}
      inputValue={inputValue}
      onInputChange={(_, v) => setInputValue(v)}
      noOptionsText={
        qTrim.length >= minLen
          ? searchLoading
            ? 'Memuat…'
            : 'Tidak ada pelanggan yang cocok.'
          : value?.documentId
            ? 'Ketik untuk mencari pelanggan lain.'
            : `Ketik minimal ${minLen} huruf atau angka untuk mencari.`
      }
      getOptionLabel={(o) => customerSearchOptionLabel(o)}
      isOptionEqualToValue={(o, v) => o.documentId === v.documentId}
      slotProps={{
        popper: {
          sx: { minWidth: 420 },
        },
        listbox: {
          sx: {
            '& .MuiAutocomplete-option': {
              alignItems: 'flex-start',
              py: 1,
              whiteSpace: 'normal',
            },
          },
        },
      }}
      renderOption={(props, option) => {
        const { key, ...rest } = props;
        const location = customerLocationLine(option);
        const contact = option.contact_name?.trim();
        const phone = customerPhoneLine(option);
        const email = option.email?.trim() || option.work_email?.trim();

        return (
          <li key={key} {...rest} data-testid={`customer-search-option-${option.documentId}`}>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.25, width: '100%' }}>
              <Typography variant="body2" component="div" sx={{ fontWeight: 600, lineHeight: 1.35 }}>
                <Box component="span" sx={{ color: 'text.secondary', fontWeight: 500 }}>
                  {option.customer_number?.trim() || '—'}
                </Box>
                {' — '}
                {option.name?.trim() || '—'}
              </Typography>
              {contact ? (
                <Typography variant="caption" component="div" color="text.secondary" sx={{ lineHeight: 1.35 }}>
                  Kontak: {contact}
                  {option.job_title?.trim() ? ` (${option.job_title.trim()})` : ''}
                </Typography>
              ) : null}
              {location ? (
                <Typography variant="caption" component="div" color="text.secondary" sx={{ lineHeight: 1.35 }}>
                  Alamat: {location}
                </Typography>
              ) : null}
              {phone || email ? (
                <Typography variant="caption" component="div" color="text.secondary" sx={{ lineHeight: 1.35 }}>
                  {[phone ? `Tel/WA: ${phone}` : null, email ? `Email: ${email}` : null].filter(Boolean).join(' · ')}
                </Typography>
              ) : null}
            </Box>
          </li>
        );
      }}
      renderInput={(params) => (
        <TextField
          {...params}
          label={label}
          required={required}
          error={Boolean(error) || Boolean(searchError && qTrim.length >= minLen)}
          helperText={mergedHelper}
          inputProps={{
            ...params.inputProps,
            ...(inputTestId ? { 'data-testid': inputTestId } : {}),
          }}
        />
      )}
    />
  );
}
